
















































import Debug from 'debug'
import { Component, Emit, Prop, Vue, Watch } from 'vue-property-decorator'
import { SerializedAnswer, SerializedScenario, SerializedScenarioNode } from '@xlearn/common'
import ScenarioResultComponent from '@/app/question-plugins/scenario/scenario-result-component.vue'
import * as Api from "@/app/question/components/api"
import { ScenarioAnswer, ScenarioScores } from './scenario-interfaces'
import { TranslateResult } from 'vue-i18n'
import i18n from '@/plugins/i18n'

interface QuestionAndAnswer {
    question: string;
    answer: string|null;
  }

  const debug = Debug('scenario-question-component')

  @Component({
    components: {
      ScenarioResultComponent
    }
  })
  export default class ScenarioQuestionComponent extends Vue implements Api.QuestionComponent<SerializedScenario, ScenarioAnswer, {}> {
    @Prop({
      required: true,
    })
    public question: Api.Question<SerializedScenario>;

    @Prop({
      required: true,
    })
    public mode: Api.QuestionMode;

    @Prop({
      required: true,
    })
    public evaluated: boolean;

    @Prop({
      required: true,
    })
    public showSolution: boolean;

    @Prop({
      required: true,
    })
    public answer: Api.Answer<ScenarioAnswer> | null;

    @Prop({
      required: true,
    })
    public state: null;

    @Prop({
      required: true
    })
    public evaluationResultType: Api.QuestionEvaluationResultType

    @Prop({
      default: true
    })
    public showButtons: boolean;

    private modalVisible: boolean = true;

    private answered: boolean = false;

    private withFigure: boolean = true;

    private questionsAndAnswers: QuestionAndAnswer[] = [];

    private currentNodeId: string|null = null;
    private currentAnswer: SerializedAnswer|null = null;
    private scoreByNodeId: ScenarioScores = {};
    private percent: number = 0;

    public selfEvaluated: boolean = false;
    public discussionPadding = 280

    @Watch('answer')
    private watchAnswer(newValue: Api.Answer<ScenarioAnswer> | null): void {
      if (!newValue) {
        this.onStart();
      }
    }

    private mounted() {
      console.log('mounted, state: ', this.state,
            'answer: ', this.answer,
            'result: ', this.evaluationResultType,
            'mode: ', this.mode,
            'question', this.question)

      if (this.answer?.answer.currentNodeId) {
        this.onStart(this.answer.answer.currentNodeId);

        this.percent = this.getScorePercent().percent;

        if (this.isFinalType) {
          this.selfEvaluated = true;
          this.modalVisible = true;
        }
      }

      window.onresize = () => this.calculatePadding()
    }

    private beforeDestroy() {
      window.onresize = () => {}
    }

    private t(key: string): TranslateResult {
      return i18n.t(key);
    }

    private get backgroundUri(): string {
      if (this.currentNode !== null) {
        return this.currentNode.images.backgroundUri;
      }

      return this.question.content.images.backgroundUri;
    }

    private get imageUri(): string {
      if (this.currentNode !== null) {
        return this.currentNode.images.imageUri;
      }

      return this.question.content.images.imageUri;
    }

    private get currentNodeHasAnswers(): boolean {
      return this.currentNode !== null && 'answers' in this.currentNode && this.currentNode.answers.length > 0;
    }

    private get currentAnswers(): SerializedAnswer[] {
      if (this.currentNode !== null && 'answers' in this.currentNode) {
        return this.currentNode.answers;
      }

      return [];
    }

    private get isFinalType(): boolean {
      if (this.currentNode === null) {
        return false;
      }

      return this.currentNode.type === 'final';
    }

    private get scenarioResults(): { [title: string]: number } {
      return {
        [this.question.content.progress.label]: this.percent
      };
    }

    private get nodes(): SerializedScenarioNode[] {
      return this.question.content.nodes;
    }

    private get currentNode(): SerializedScenarioNode|null {
      if (this.currentNodeId === null) {
        return null;
      }

      return this.nodes.find((n) => n.id === this.currentNodeId) as SerializedScenarioNode;
    }

    private calculatePadding(): void {
      const choicesHeight = (this.$refs.choicesContent as HTMLElement)?.getBoundingClientRect()?.height || 280
      this.discussionPadding = Math.round(choicesHeight)
      this.scrollToEnd(() => {})
    }

    private onStart(startNodeId?: string): void {
      this.retry(startNodeId);
      this.modalVisible = false;
    }

    private onRestart(): void {
      this.emitReset();
    }

    private onAnswer(answer: SerializedAnswer): void {
      this.answered = true;
      this.addAnswer(answer.answer);

      this.currentAnswer = answer;

      if (this.currentNodeId !== null) {
        this.scoreByNodeId[this.currentNodeId] = answer.point;
      }

      this.showNext();
      this.emitAnswerChanged();

      setTimeout(() => {
        this.$nextTick(() => this.calculatePadding())
      }, 1300)

      if (this.isFinalType) {
        this.evaluate();
      }
    }

    private showNext(): void {
      if (this.currentAnswer !== null) {
        this.moveToNodeById(this.currentAnswer.nextNodeId);
      }

      this.scrollToEnd(() => this.answered = false);
    }

    private evaluate(): void {
      this.modalVisible = true;
      this.selfEvaluated = true;

      this.emitSelfEvaluated();
    }

    public emitStateChanged(): Api.StateChangedPayload<{}> {
      return {
        state: {}
      };
    }

    @Emit('answer-changed')
    public emitAnswerChanged(): Api.AnswerChangedPayload<ScenarioAnswer> {
      const { score, percent } = this.getScorePercent();

      this.percent = percent;

      return {
        answer: {
          currentNodeId: this.currentNodeId as string,
          scoreByNodeId: this.scoreByNodeId,
        },
        score,
      };
    }

    @Emit('self-evaluated')
    public emitSelfEvaluated() {}

    @Emit('self-reset')
    public emitReset() {}

    private getScorePercent() {
      const score: number = Object.values(this.scoreByNodeId).reduce((total: number, current: number) => total + current, 0);
      const result = this.percentAndScaledScore(score);

      return {
        score,
        percent: result.percent,
      }
    }

    // REVIEW: #cleanup a startNodeId?: string paraméter nincs használva
    private retry(startNodeId?: string): void {
      this.modalVisible = false;
      this.answered = false;
      this.selfEvaluated = false;
      this.scoreByNodeId = this.answer?.answer.scoreByNodeId || {};
      this.questionsAndAnswers = [];
      this.currentNodeId = this.answer?.answer.currentNodeId || this.question.content.startNodeId;

      this.moveToNodeById(this.currentNodeId);
    }

    private scrollToEnd(callback: () => void): void {
      const $container = $(<HTMLElement>this.$refs.scrollContainer);
      const content = (<HTMLElement>this.$refs.scrollContent);

      if (!content) return
      $container.animate({
        scrollTop: content.offsetHeight
      }, 1000, () => callback());
    }

    private addQuestion(question: string): void {
      this.questionsAndAnswers = [...this.questionsAndAnswers, {
        question,
        answer: null
      }];
    }

    private addAnswer(answer: string): void {
      let newQuestionsAndAnswers: QuestionAndAnswer[] = [];
      const length: number = this.questionsAndAnswers.length;
      for (let i = 0; i < length; i++) {
        const item: QuestionAndAnswer = this.questionsAndAnswers[i];

        const isLast: boolean = i + 1 === length;
        if (isLast) {
          item.answer = answer;
        }

        newQuestionsAndAnswers = [...newQuestionsAndAnswers, item];
      }

      this.questionsAndAnswers = [...newQuestionsAndAnswers];
    }

    private moveToNodeById(id: string): void {
      this.currentNodeId = id;

      if (this.currentNode !== null && this.currentNodeHasAnswers) {
        this.addQuestion(this.currentNode.text);
      }
    }

    private percentAndScaledScore(score: number): { percent: number } {
      const point = this.question.content.point;
      const normalized: number = score - point.min;
      const diff: number = point.max - point.min;

      let rate: number = normalized / diff;
      // TODO: how to do it?
      if (point.reverse) {
        rate = (point.max - normalized) / diff;
      }
      const percent: number = Math.round(100 * rate);

      return {
        percent
      };
    }
  }
